import { api, getAuthToken, getClientId, API_BASE_URL } from '../../../api/client';
import { io, Socket } from 'socket.io-client';
import type {
  Element,
  ElementInput,
  RunningElement,
  RunningState,
  Schedule,
  ScheduleEntry,
  ScheduleEntryInput,
  VarianceSummary,
  DayOfWeek,
  ObservationSource,
} from '@thekwallace/backtime-shared';

// Re-export getClientId for convenience (defined in api/client.ts)
export { getClientId };

// WebSocket connection
let socket: Socket | null = null;
let currentTimelineId: string | null = null; // Track current timeline for reconnection
let wsConnectionListeners: Set<(connected: boolean) => void> = new Set(); // Listeners for connection status changes
let lastSequences: Map<string, number> = new Map(); // Track last seen sequence per timeline for gap detection

/**
 * Last seen `running-state-updated` sequence per timeline.
 *
 * DELIBERATELY SEPARATE from `lastSequences` (plan §7). The server gives
 * running-state broadcasts their own counter, so folding them into the
 * `timeline-updated` map would make every mark look like lost timeline messages
 * and trigger a spurious full resync on the next real one. Bootstrapped from
 * `get-running-sequence`, the sibling of `get-sequence`.
 */
let lastRunningSequences: Map<string, number> = new Map();

// =============================================================================
// SCHEDULE CONVERSION HELPERS
// =============================================================================

/**
 * Convert Schedule (UI format) to ScheduleEntryInput[] (API format)
 */
export function scheduleToEntries(schedule: Schedule): ScheduleEntryInput[] {
  const entries: ScheduleEntryInput[] = [];
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  for (const day of days) {
    const times = schedule[day] || [];
    for (const time of times) {
      entries.push({ dayOfWeek: day, time });
    }
  }

  return entries;
}

/**
 * Convert ScheduleEntry[] (API format) to Schedule (UI format)
 */
export function entriesToSchedule(entries: ScheduleEntry[]): Schedule {
  const schedule: Schedule = {};

  for (const entry of entries) {
    const day = entry.dayOfWeek;
    if (!schedule[day]) {
      schedule[day] = [];
    }
    schedule[day]!.push(entry.time);
  }

  // Sort times within each day
  for (const day of Object.keys(schedule) as DayOfWeek[]) {
    schedule[day]?.sort();
  }

  return schedule;
}

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Timeline data returned from API
 * Uses typed elements and scheduleEntries (not JSON phases/schedule)
 */
export interface TimelineData {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  ownerId: string;
  isPublic: boolean;
  publicCode: string | null;
  elements: Element[];
  scheduleEntries: ScheduleEntry[];
  selectedStartTime?: string | null;
  timeOffset: number;
  timezone?: string;
  activeStartTime?: string | null;
  autoAdvanceBuffer: number;
  isAutoAdvanceEnabled: boolean;
  urgencySettings?: {
    imminent?: { thresholdMinutes: number; color?: string };
    soon?: { thresholdMinutes: number; color?: string };
  } | null;
  isSubscribed?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Timeline list item (summary)
 */
export interface TimelineListItem {
  id: string;
  name: string;
  ownerId: string;
  isPublic: boolean;
  publicCode?: string | null;
  updatedAt: string;
}

/**
 * Create timeline input
 */
export interface CreateTimelineInput {
  name?: string;
  isPublic?: boolean;
  elements?: ElementInput[];
  scheduleEntries?: ScheduleEntryInput[];
  selectedStartTime?: string | null;
  timeOffset?: number;
  timezone?: string;
  autoActivate?: boolean;
}

/**
 * Update timeline input
 */
export interface UpdateTimelineInput {
  name?: string;
  description?: string | null;
  color?: string | null;
  isPublic?: boolean;
  elements?: ElementInput[];
  scheduleEntries?: ScheduleEntryInput[];
  selectedStartTime?: string | null;
  timeOffset?: number;
  timezone?: string;
  isAutoAdvanceEnabled?: boolean;
  autoAdvanceBuffer?: number;
}

/**
 * WebSocket update data
 */
interface TimelineUpdateData {
  timelineId: string;
  sequence?: number;
  timestamp?: string;
  sourceClientId?: string;
  timeline?: TimelineData;
  data?: Partial<TimelineData>;
}

/**
 * Resync required data
 */
interface ResyncData {
  timelineId: string;
  currentSequence?: number;
}

/**
 * `running-state-updated` envelope — deliberately the same shape as
 * `timeline-updated` so client handling is symmetric.
 * `data` is the FULL RunningState, never a delta.
 */
export interface RunningStateUpdateData {
  timelineId: string;
  sequence?: number;
  timestamp?: string;
  data?: RunningState | null;
}

/**
 * Response envelope shared by all three running-state endpoints.
 *
 * The server projects `elements`/`summary` for convenience, but they are only
 * accurate as of `nowSeconds` — the moment the response was built. This client
 * therefore re-projects locally on every render tick (see TimeElementList) and
 * treats `runningState` as the single source of truth. One code path, always
 * fresh, no "is this stale?" question to get wrong.
 */
export interface RunningStateResponse {
  runningState: RunningState | null;
  /**
   * Occurrence start in the SERVER's frame — see the note below. This client
   * deliberately ignores it; localize `runningState.occurrenceStart` instead.
   */
  occurrenceStartSeconds: number | null;
  /** Server "now" in the SERVER's frame, at response time. Also ignored here. */
  nowSeconds: number | null;
  elements: RunningElement[];
  summary: VarianceSummary;
}

/**
 * TIME FRAME (pinned with the API, shared 0.9.0, plan §6.1).
 *
 * **On the wire, times are ISO-8601 INSTANTS.** `RunningState.occurrenceStart`,
 * `startedAt`, and every observation's `actualStart` / `recordedAt` are absolute
 * instants. An instant belongs to no timezone, so there is nothing for the two
 * ends to disagree about — which is the entire point of the format.
 *
 * They were previously seconds from midnight of the occurrence's local day in the
 * **timeline's** timezone. That frame is coherent on the server, which knows the
 * timeline's zone, but this app renders in seconds from the **browser's** local
 * midnight. Whenever the two zones differed, planned and actual were compared
 * across frames and every running-state number came out wrong by the offset —
 * measured in a real browser as a 633 s server variance rendering "+3:10:33
 * behind", and a row reading "Started at: 10:57 PM" on a device showing 7:57 PM.
 *
 * The envelope's `occurrenceStartSeconds` / `nowSeconds` are the ONLY second
 * counts left, and they are still in the server's own frame — the API keeps
 * working in timeline-timezone seconds internally, which is self-consistent for
 * it. They are convenience values for a same-zone client and this app must not
 * consume them: it localizes the instants itself
 * (`utils/runningState.ts` → `localizeRunningStateForBrowser`). The same applies
 * to the server-projected `elements` / `summary`, which are only accurate as of
 * `nowSeconds` anyway — this client re-projects locally every tick.
 */

/**
 * Normalize a running-state payload to `RunningState | null`.
 *
 * Accepts the documented `{ runningState, ... }` envelope, and also a bare
 * RunningState (the shape the WebSocket `data` field carries).
 */
export function unwrapRunningState(payload: unknown): RunningState | null {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;

  // Wrapped: { runningState: RunningState | null }
  if ('runningState' in record) {
    return unwrapRunningState(record.runningState);
  }

  // Bare RunningState — identified by its append-only observation log.
  if (Array.isArray(record.observations) && typeof record.timelineId === 'string') {
    return payload as RunningState;
  }

  return null;
}

/**
 * Notify all connection listeners of status change
 */
function notifyConnectionListeners(connected: boolean): void {
  wsConnectionListeners.forEach(listener => {
    try {
      listener(connected);
    } catch (error) {
      console.error('Error in connection status listener:', error);
    }
  });
}

/**
 * Timeline service for managing shared service timer timelines
 * Handles timeline CRUD, public sharing, and real-time updates
 */
const timelineService = {
  /**
   * Initialize WebSocket connection
   */
  initSocket(): Socket {
    if (!socket) {
      const token = getAuthToken();
      socket = io(API_BASE_URL, {
        autoConnect: false,
        auth: {
          token: token
        },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });

      // Rejoin timeline room on reconnection
      socket.on('connect', () => {
        console.log('WebSocket connected');
        notifyConnectionListeners(true);
        if (currentTimelineId) {
          console.log('Rejoining timeline:', currentTimelineId);
          socket!.emit('join-timeline', currentTimelineId);
        }
      });

      socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
        notifyConnectionListeners(false);
      });

      socket.on('reconnect', (attemptNumber) => {
        console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      });

      socket.on('reconnect_error', (error) => {
        console.log('WebSocket reconnect error:', error.message);
      });
    }
    return socket;
  },

  /**
   * Get socket instance
   */
  getSocket(): Socket {
    if (!socket) {
      this.initSocket();
    }
    return socket!;
  },

  /**
   * Generate a short public code for timeline sharing (6 characters)
   */
  generatePublicCode(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  },

  /**
   * Create a new timeline
   */
  async createTimeline(_ownerId: string, timelineData: CreateTimelineInput = {}): Promise<string> {
    try {
      // Get user's timezone from browser
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Default schedule entries (Sunday times)
      const defaultScheduleEntries: ScheduleEntryInput[] = [
        { dayOfWeek: 'sunday', time: '08:30:00' },
        { dayOfWeek: 'sunday', time: '10:00:00' },
        { dayOfWeek: 'sunday', time: '11:30:00' },
        { dayOfWeek: 'sunday', time: '13:00:00' },
      ];

      const response = await api.post<{ timeline: TimelineData }>('/timelines', {
        name: timelineData.name || 'My Timeline',
        isPublic: false,
        elements: timelineData.elements || [],
        scheduleEntries: timelineData.scheduleEntries || defaultScheduleEntries,
        selectedStartTime: timelineData.selectedStartTime || null,
        timeOffset: timelineData.timeOffset || 0,
        timezone: timelineData.timezone || userTimezone || 'America/Chicago'
      });

      return response.timeline.id;
    } catch (error) {
      console.error('Error creating timeline:', error);
      throw error;
    }
  },

  /**
   * Get timeline by ID
   */
  async getTimeline(timelineId: string): Promise<TimelineData> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.get<{ timeline: TimelineData }>(`/timelines/${timelineId}`);
      return response.timeline;
    } catch (error) {
      console.error('Error getting timeline:', error);
      throw error;
    }
  },

  /**
   * Get timeline by public code
   */
  async getTimelineByPublicCode(publicCode: string): Promise<TimelineData | null> {
    if (!publicCode) {
      throw new Error('Public code is required');
    }

    try {
      const response = await api.get<{ timeline: TimelineData }>(`/timelines/public/${publicCode}`);
      return response.timeline;
    } catch (error) {
      console.error('Error getting timeline by public code:', error);
      return null;
    }
  },

  /**
   * Update timeline data
   * @param timelineId - The timeline ID to update
   * @param updates - The fields to update
   * @param options - Optional settings including ifUnmodifiedSince for conflict detection
   */
  async updateTimeline(
    timelineId: string,
    updates: UpdateTimelineInput,
    options?: { ifUnmodifiedSince?: string }
  ): Promise<TimelineData> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.put<{ timeline: TimelineData }>(
        `/timelines/${timelineId}`,
        updates,
        options
      );
      return response.timeline;
    } catch (error) {
      console.error('Error updating timeline:', error);
      throw error;
    }
  },

  /**
   * Make timeline public or private
   */
  async setTimelinePublic(timelineId: string, isPublic: boolean): Promise<TimelineData> {
    return this.updateTimeline(timelineId, { isPublic });
  },

  /**
   * Get user's active timeline ID
   */
  async getUserActiveTimeline(userId: string): Promise<string | null> {
    if (!userId) {
      return null;
    }

    try {
      const user = await api.get<{ activeTimelineId?: string }>('/auth/me');

      // If user has an active timeline, return it
      if (user.activeTimelineId) {
        return user.activeTimelineId;
      }

      // Fallback: check if user has any owned timelines
      try {
        const { timelines } = await api.get<{ timelines: TimelineListItem[] }>(`/users/${userId}/timelines`);
        if (timelines && timelines.length > 0) {
          // Return the most recently updated timeline
          return timelines[0].id;
        }
      } catch (err) {
        console.error('Error fetching user timelines:', err);
      }

      return null;
    } catch (error) {
      console.error('Error getting user active timeline:', error);
      return null;
    }
  },

  /**
   * Subscribe to real-time timeline updates via WebSocket
   * @param timelineId - Timeline to subscribe to
   * @param callback - Called with timeline data and optional sourceClientId (for detecting own updates)
   */
  subscribeToTimeline(timelineId: string, callback: (timeline: TimelineData | Partial<TimelineData> | null, sourceClientId?: string) => void): () => void {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    const socket = this.getSocket();

    // Track current timeline for reconnection
    currentTimelineId = timelineId;

    // Update auth token before connecting (in case it changed)
    const token = getAuthToken();
    if (token) {
      socket.auth = { token };
    }

    // Connect if not already connected
    if (!socket.connected) {
      socket.connect();
    }

    // Join the timeline room (use new event name)
    socket.emit('join-timeline', timelineId);

    // Get initial sequence number for this timeline
    socket.emit('get-sequence', timelineId, (sequence: number) => {
      console.log(`Initial sequence for timeline ${timelineId}: ${sequence}`);
      lastSequences.set(timelineId, sequence);
    });

    // Listen for timeline updates with acknowledgement support
    const onTimelineUpdate = (data: TimelineUpdateData, ackCallback?: (ack: { received: boolean; sequence: number }) => void) => {
      if (data.timelineId === timelineId) {
        const { sequence, timestamp } = data;

        // Check for sequence gaps if we have a previous sequence
        if (sequence !== undefined) {
          const lastSeq = lastSequences.get(timelineId) || 0;

          if (sequence > lastSeq + 1 && lastSeq > 0) {
            // Gap detected - we missed some messages
            console.warn(`Sequence gap detected for timeline ${timelineId}: expected ${lastSeq + 1}, got ${sequence}`);
            // Request resync
            socket.emit('request-resync', { timelineId, lastSequence: lastSeq });
          }

          // Update last seen sequence
          lastSequences.set(timelineId, sequence);

          console.log(`Received update for timeline ${timelineId}, sequence ${sequence}${timestamp ? `, timestamp ${new Date(timestamp).toISOString()}` : ''}`);
        }

        // Send acknowledgement if callback provided
        if (ackCallback && typeof ackCallback === 'function') {
          ackCallback({ received: true, sequence: sequence || 0 });
          console.log(`Sent ack for timeline ${timelineId}, sequence ${sequence}`);
        }

        // Handle both full timeline updates and partial updates
        // Pass sourceClientId so caller can detect their own updates
        if (data.timeline) {
          callback(data.timeline, data.sourceClientId);
        } else if (data.data) {
          // Partial update (like activeStartTime change)
          callback(data.data, data.sourceClientId);
        }
      }
    };

    // Handle resync required event
    const onResyncRequired = (data: ResyncData) => {
      if (data.timelineId === timelineId) {
        console.log(`Resync required for timeline ${timelineId}, fetching full state...`);
        // Fetch full timeline state from REST API
        this.getTimeline(timelineId)
          .then(timeline => {
            // Update sequence to current
            if (data.currentSequence !== undefined) {
              lastSequences.set(timelineId, data.currentSequence);
            }
            callback(timeline);
            console.log(`Resync complete for timeline ${timelineId}`);
          })
          .catch(error => {
            console.error('Error during resync:', error);
          });
      }
    };

    socket.on('timeline-updated', onTimelineUpdate);
    socket.on('resync-required', onResyncRequired);

    // Fetch initial timeline data
    this.getTimeline(timelineId)
      .then(timeline => callback(timeline))
      .catch(error => {
        console.error('Error fetching initial timeline:', error);
        callback(null);
      });

    // Return unsubscribe function
    return () => {
      socket.off('timeline-updated', onTimelineUpdate);
      socket.off('resync-required', onResyncRequired);
      socket.emit('leave-timeline', timelineId);
      // Clear tracked timeline if it matches
      if (currentTimelineId === timelineId) {
        currentTimelineId = null;
      }
      // Clean up sequence tracking
      lastSequences.delete(timelineId);
    };
  },

  // ===========================================================================
  // RUNNING STATE (Operate Mode)
  // ===========================================================================

  /**
   * Get the current running state for a timeline, or null if no occurrence is
   * being operated.
   *
   * Readable by any viewer of the timeline (including public-timeline viewers) —
   * only the WRITES below are owner-gated.
   */
  async getRunningState(timelineId: string): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.get<RunningStateResponse>(`/timelines/${timelineId}/running-state`);
      return unwrapRunningState(response);
    } catch (error) {
      // A timeline with nothing running is the normal case, not an error.
      if ((error as { status?: number }).status === 404) return null;
      console.error('Error getting running state:', error);
      throw error;
    }
  },

  /**
   * Start operating an occurrence. Owner only.
   *
   * Idempotent server-side: 201 on a fresh start, 200 if already running. Both
   * are successes and both return the current state, so there is nothing to
   * branch on here.
   *
   * @param timelineId - Timeline to operate
   * @param occurrenceStart - Occurrence identity as an absolute ISO-8601 instant
   */
  async startRunningState(timelineId: string, occurrenceStart: string): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    if (!occurrenceStart) {
      throw new Error('Occurrence start is required');
    }

    try {
      const response = await api.post<RunningStateResponse>(
        `/timelines/${timelineId}/running-state`,
        { occurrenceStart }
      );
      return unwrapRunningState(response);
    } catch (error) {
      console.error('Error starting running state:', error);
      throw error;
    }
  },

  /**
   * Mark a transition — record that an element ACTUALLY started. Owner only.
   *
   * `actualStart` is OMITTED for a live mark so the server stamps it from its own
   * clock. That remains the right default even now that the wire format is
   * timezone-free: the server's clock is the one every other client is already
   * synced to, and a live mark's "now" is not something the client knows better.
   *
   * The parameter exists for retroactive correction/backfill (Slice 3), where the
   * caller genuinely knows WHEN and must say so. It is an absolute ISO-8601
   * INSTANT (plan §6.1) — callers hold browser-frame seconds and convert with
   * `absoluteSecondsToIsoInstant` first. Passing a raw second count used to be the
   * signature here, and it was unsafe by construction: the number meant
   * seconds-from-midnight in the timeline's zone, the caller had one measured from
   * the browser's, and nothing in the types could tell them apart.
   */
  async markRunningTransition(
    timelineId: string,
    elementId: string,
    actualStart?: string,
    source?: ObservationSource
  ): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    if (!elementId) {
      throw new Error('Element ID is required');
    }

    try {
      const body: {
        elementId: string;
        actualStart?: string;
        source?: ObservationSource;
      } = { elementId };
      if (actualStart !== undefined) {
        body.actualStart = actualStart;
      }
      // Omitted for a live mark so the server applies its own default, rather than this
      // client asserting a default the API owns.
      if (source !== undefined) {
        body.source = source;
      }
      const response = await api.post<RunningStateResponse>(
        `/timelines/${timelineId}/running-state/mark`,
        body
      );
      return unwrapRunningState(response);
    } catch (error) {
      const status = (error as { status?: number }).status;
      // 409 means "nothing is running" — a recoverable state, not a crash.
      if (status === 409) {
        const conflict = new Error('The occurrence is not running yet. Press Start first.');
        (conflict as Error & { status?: number; code?: string }).status = 409;
        (conflict as Error & { status?: number; code?: string }).code = 'NOT_RUNNING';
        throw conflict;
      }
      // 404 means the element is no longer on the timeline.
      if (status === 404) {
        const missing = new Error('That element is no longer part of this timeline.');
        (missing as Error & { status?: number; code?: string }).status = 404;
        (missing as Error & { status?: number; code?: string }).code = 'ELEMENT_NOT_FOUND';
        throw missing;
      }
      console.error('Error marking running transition:', error);
      throw error;
    }
  },

  /**
   * State that an element is STILL RUNNING.
   *
   * The statement `mark` cannot make. `mark` says when something began and says nothing
   * about whether it has finished, so on its own the app only ever learns of an over-run
   * after the fact — by being told what started next. A hold pins the playhead and lets
   * over-run accrue live, without the app ever inferring one from the clock.
   *
   * Carries no `actualStart`: a hold must not assert a start, or it could rewrite the
   * very history it is describing.
   */
  async holdRunningElement(
    timelineId: string,
    elementId: string
  ): Promise<RunningState | null> {
    return this.markRunningTransition(timelineId, elementId, undefined, 'hold');
  },

  /**
   * Re-apply the most recently undone observation.
   *
   * 409 covers both "nothing running" and "nothing to redo" — from the client's point of
   * view they are the same answer.
   */
  async redoLastRunningObservation(timelineId: string): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.post<RunningStateResponse>(
        `/timelines/${timelineId}/running-state/observations/redo`,
        {}
      );
      return unwrapRunningState(response);
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        const conflict = new Error('There is nothing to redo.');
        (conflict as Error & { status?: number; code?: string }).status = 409;
        (conflict as Error & { status?: number; code?: string }).code = 'NOT_RUNNING';
        throw conflict;
      }
      console.error('Error redoing running observation:', error);
      throw error;
    }
  },

  /**
   * Retract what was recorded for one element.
   *
   * This is the unlock for editing history. An element the run has reached is locked —
   * moving or retiming it would leave an observation pointing at something that changed
   * underneath it — and clearing moves it out of that set.
   *
   * Append-only on the server: a clear is an observation saying "nothing was recorded
   * for this after all", so `undoLastRunningObservation` puts it back. Clearing the
   * element under the playhead hands the playhead to the previous surviving mark.
   */
  async clearRunningObservation(
    timelineId: string,
    elementId: string
  ): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    if (!elementId) {
      throw new Error('Element ID is required');
    }

    try {
      const response = await api.delete<RunningStateResponse>(
        `/timelines/${timelineId}/running-state/observations/${elementId}`
      );
      return unwrapRunningState(response);
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        const conflict = new Error('There is no running occurrence.');
        (conflict as Error & { status?: number; code?: string }).status = 409;
        (conflict as Error & { status?: number; code?: string }).code = 'NOT_RUNNING';
        throw conflict;
      }
      console.error('Error clearing running observation:', error);
      throw error;
    }
  },

  /**
   * Drop the most recent observation — Undo.
   *
   * 409 covers both "nothing running" and "nothing left to drop": from the client's
   * point of view they are the same answer, which is that the transition it asked for
   * does not exist from here.
   */
  async undoLastRunningObservation(timelineId: string): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.delete<RunningStateResponse>(
        `/timelines/${timelineId}/running-state/observations/last`
      );
      return unwrapRunningState(response);
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        const conflict = new Error('There is nothing to undo.');
        (conflict as Error & { status?: number; code?: string }).status = 409;
        (conflict as Error & { status?: number; code?: string }).code = 'NOT_RUNNING';
        throw conflict;
      }
      console.error('Error undoing running observation:', error);
      throw error;
    }
  },

  /**
   * Complete the current occurrence. Owner only.
   *
   * The state stays READABLE after completing (plan §6.3) so a viewer can still
   * see how the run finished; it is `abandon` that removes it. The projection
   * stops stretching the in-progress element the moment status leaves
   * `'running'`, so the numbers freeze where the run actually ended.
   *
   * 409 means there was nothing to complete — recoverable, not a crash.
   */
  async completeRunningState(timelineId: string): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.post<RunningStateResponse>(
        `/timelines/${timelineId}/running-state/complete`,
        {}
      );
      return unwrapRunningState(response);
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        const conflict = new Error('There is no running occurrence to complete.');
        (conflict as Error & { status?: number; code?: string }).status = 409;
        (conflict as Error & { status?: number; code?: string }).code = 'NOT_RUNNING';
        throw conflict;
      }
      console.error('Error completing running state:', error);
      throw error;
    }
  },

  /**
   * Abandon (discard) the current occurrence. Owner only.
   *
   * Removes the state outright rather than flagging it, which is what lets a
   * mis-started occurrence be restarted cleanly — `start` is idempotent, so
   * without removal a wrong Start could never be undone (plan §6.3). Works on a
   * COMPLETED run too: that is how an operator clears a finished run off
   * viewers' screens.
   *
   * The API answers 200 with `runningState: null`, so this resolves to `null` on
   * success — which is indistinguishable from the "nothing running" read. That is
   * intentional: after abandoning, the timeline's running state IS nothing.
   */
  async abandonRunningState(timelineId: string): Promise<RunningState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.delete<RunningStateResponse>(
        `/timelines/${timelineId}/running-state`
      );
      return unwrapRunningState(response);
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        const conflict = new Error('There is no occurrence to discard.');
        (conflict as Error & { status?: number; code?: string }).status = 409;
        (conflict as Error & { status?: number; code?: string }).code = 'NOT_RUNNING';
        throw conflict;
      }
      console.error('Error abandoning running state:', error);
      throw error;
    }
  },

  /**
   * Sequence tracking for this stream is SEPARATE from `subscribeToTimeline`'s
   * (plan §7): the server runs an independent counter for `running-state-updated`,
   * bootstrapped here via `get-running-sequence`. Folding the two together made
   * every mark burn a number the `timeline-updated` stream never delivered, so the
   * next real timeline update looked like lost messages and forced a full resync.
   *
   * A gap here recovers over REST rather than through `request-resync`, which is
   * timeline-scoped: re-`GET`ting is already an exact recovery because the payload
   * is full state, never a delta.
   */
  subscribeToRunningState(
    timelineId: string,
    callback: (state: RunningState | null) => void
  ): () => void {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    const socket = this.getSocket();

    // Update auth token before connecting (in case it changed)
    const token = getAuthToken();
    if (token) {
      socket.auth = { token };
    }

    if (!socket.connected) {
      socket.connect();
    }

    // Joining is idempotent server-side; safe alongside subscribeToTimeline.
    socket.emit('join-timeline', timelineId);

    // Bootstrap this stream's own counter. An API that predates running state has
    // no handler and simply never calls back, leaving us at 0 — which disables gap
    // detection rather than reporting a false gap.
    socket.emit('get-running-sequence', timelineId, (sequence: number) => {
      if (typeof sequence === 'number') {
        lastRunningSequences.set(timelineId, sequence);
      }
    });

    const onRunningStateUpdate = (
      payload: RunningStateUpdateData,
      ackCallback?: (ack: { received: boolean; sequence: number }) => void
    ) => {
      if (payload?.timelineId !== timelineId) return;

      const { sequence } = payload;

      if (ackCallback && typeof ackCallback === 'function') {
        ackCallback({ received: true, sequence: sequence || 0 });
      }

      if (sequence !== undefined) {
        const lastSeq = lastRunningSequences.get(timelineId) || 0;

        // Only a forward gap is a gap. A repeat or a retry arriving out of order
        // must not re-fetch, and must not drag the tracked sequence backward.
        if (sequence > lastSeq + 1 && lastSeq > 0) {
          console.warn(
            `Running-state sequence gap for timeline ${timelineId}: expected ${lastSeq + 1}, got ${sequence}`
          );
          this.getRunningState(timelineId)
            .then(callback)
            .catch((error) => console.error('Error resyncing running state:', error));
        }

        if (sequence > lastSeq) {
          lastRunningSequences.set(timelineId, sequence);
        }
      }

      callback(unwrapRunningState(payload.data));
    };

    socket.on('running-state-updated', onRunningStateUpdate);

    // Fetch initial state. A failure here must not break the timeline view —
    // an API that predates running state simply leaves us with null.
    this.getRunningState(timelineId)
      .then(callback)
      .catch((error) => {
        console.error('Error fetching initial running state:', error);
        callback(null);
      });

    return () => {
      socket.off('running-state-updated', onRunningStateUpdate);
      lastRunningSequences.delete(timelineId);
    };
  },

  /**
   * Check if user can edit a timeline
   */
  async canUserEditTimeline(userId: string, timelineId: string): Promise<boolean> {
    if (!userId || !timelineId) {
      return false;
    }

    try {
      const timeline = await this.getTimeline(timelineId);
      return timeline && timeline.ownerId === userId;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  },

  /**
   * Get all user timelines (owned, subscribed, or all)
   */
  async getAllUserTimelines(userId: string, type: 'owned' | 'subscribed' | 'all' = 'all'): Promise<TimelineListItem[]> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const response = await api.get<{ timelines: TimelineListItem[] }>(`/users/${userId}/timelines?type=${type}`);
      return response.timelines || [];
    } catch (error) {
      console.error('Error getting user timelines:', error);
      throw error;
    }
  },

  /**
   * Set active timeline for user
   */
  async setActiveTimeline(userId: string, timelineId: string): Promise<{ id: string }> {
    if (!userId || !timelineId) {
      throw new Error('User ID and Timeline ID are required');
    }

    try {
      const response = await api.put<{ user: { id: string } }>(`/users/${userId}/active-timeline`, {
        timelineId
      });
      return response.user;
    } catch (error) {
      console.error('Error setting active timeline:', error);
      throw error;
    }
  },

  /**
   * Duplicate a timeline
   */
  async duplicateTimeline(timelineId: string, newName: string): Promise<TimelineData> {
    if (!timelineId || !newName) {
      throw new Error('Timeline ID and new name are required');
    }

    try {
      const response = await api.post<{ timeline: TimelineData }>(`/timelines/${timelineId}/duplicate`, {
        name: newName
      });
      return response.timeline;
    } catch (error) {
      console.error('Error duplicating timeline:', error);
      throw error;
    }
  },

  /**
   * Update public code for a timeline
   */
  async updatePublicCode(timelineId: string, publicCode: string): Promise<TimelineData> {
    if (!timelineId || !publicCode) {
      throw new Error('Timeline ID and public code are required');
    }
    try {
      const response = await api.put<{ timeline: TimelineData }>(`/timelines/${timelineId}/public-code`, {
        publicCode: publicCode.toUpperCase()
      });
      return response.timeline;
    } catch (error) {
      console.error('Error updating public code:', error);
      throw error;
    }
  },

  /**
   * Delete a timeline
   */
  async deleteTimeline(timelineId: string): Promise<boolean> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    try {
      await api.delete(`/timelines/${timelineId}`);
      return true;
    } catch (error) {
      console.error('Error deleting timeline:', error);
      throw error;
    }
  },

  /**
   * Subscribe to a public timeline (authenticated users)
   */
  async subscribeToTimelineById(timelineId: string): Promise<{ message: string }> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    try {
      const response = await api.post<{ message: string }>(`/timelines/${timelineId}/subscribe`);
      return response;
    } catch (error) {
      console.error('Error subscribing to timeline:', error);
      throw error;
    }
  },

  /**
   * Unsubscribe from a timeline (authenticated users)
   */
  async unsubscribeFromTimeline(timelineId: string): Promise<boolean> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    try {
      await api.delete(`/timelines/${timelineId}/subscribe`);
      return true;
    } catch (error) {
      console.error('Error unsubscribing from timeline:', error);
      throw error;
    }
  },

  /**
   * Migrate local subscriptions to server (called after login)
   */
  async migrateLocalSubscriptions(localSubscriptions: Array<{ publicCode: string; name?: string }>): Promise<{ migrated: number; failed: number }> {
    if (!localSubscriptions || localSubscriptions.length === 0) {
      return { migrated: 0, failed: 0 };
    }

    let migrated = 0;
    let failed = 0;

    for (const sub of localSubscriptions) {
      try {
        // First get the timeline by public code to get its ID
        const timeline = await this.getTimelineByPublicCode(sub.publicCode);
        if (timeline && timeline.id) {
          await this.subscribeToTimelineById(timeline.id);
          migrated++;
        } else {
          failed++;
        }
      } catch (error) {
        console.error(`Failed to migrate subscription ${sub.publicCode}:`, error);
        failed++;
      }
    }

    return { migrated, failed };
  },

  /**
   * Check if WebSocket is connected
   */
  isWebSocketConnected(): boolean {
    return socket?.connected || false;
  },

  /**
   * Subscribe to WebSocket connection status changes
   */
  subscribeToConnectionStatus(callback: (connected: boolean) => void): () => void {
    wsConnectionListeners.add(callback);
    // Immediately call with current status
    callback(this.isWebSocketConnected());
    return () => wsConnectionListeners.delete(callback);
  }
};

export default timelineService;
